/**
 * Render PSD — one-shot, non-destructive: reconstruct the painted artwork +
 * symmetry from a PSD (brief 03-procreate.md "Rendering"/00-WORK.md §2), then
 * produce one hi-res tessellated PNG via the exact same renderer Wallpaper
 * export uses. Never touches the user's live sketch — every store mutated
 * below is snapshotted first and restored in `finally`, even on error.
 */
import { readPsd } from 'ag-psd';
import { getWallpaperGroup, type PatternConfig } from '@/patterns/wallpaper';
import { downloadCanvasAsPng } from '@/services/download';
import { exportFilename } from '@/services/exportFilename';
import { checkAgainstFile, decodeDataImages, validateData } from '@/services/psd/psdData';
import { dataLayerImages, extractPaintRegions, hasPaintLayers, psdDebug } from '@/services/psd/psdImport';
import { validateRenderMetadata } from '@/services/psd/psdMetadata';
import { renderWallpaperTiles } from '@/services/tileRenderer';
import { tilesAcrossToRings } from '@/services/wallpaperExport';
import { TILE_RASTER, useLayersStore } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import { useToolsStore } from '@/stores/tools';

export interface PsdRenderSummary {
  imported: string[];
  missingRegions: string[];
  unmatched: string[];
}

export interface RenderPsdOptions {
  /** "how many tiles across" shorthand — same field Wallpaper export uses. */
  tilesAcross: number;
}

export async function renderPsd(source: File | ArrayBuffer, opts: RenderPsdOptions): Promise<PsdRenderSummary> {
  const buffer = source instanceof File ? await source.arrayBuffer() : source;
  const psd = readPsd(buffer, { skipThumbnail: true });
  psdDebug('readPsd', { width: psd.width, height: psd.height, layers: psd.children?.map((l) => l.name) });

  // Metadata is the QR pixel layer(s) — the only channel that survives a
  // Procreate round-trip (brief §2). XMP is a non-load-bearing cross-check
  // below; it is NEVER a substitute for the QR decode. Each failure mode gets
  // its own message; never a silent fallback (brief §3).
  const dataImages = dataLayerImages(psd);
  if (dataImages.length === 0) {
    throw new Error(
      hasPaintLayers(psd)
        ? 'This PSD is missing its Escherllate data layer(s) — they may have been deleted. Re-export it from Escherllate.'
        : "This PSD isn't an Escherllate export (no data or paint layers found).",
    );
  }
  const payload = decodeDataImages(dataImages);
  if (payload === null) {
    throw new Error(
      'Couldn’t read the Escherllate data code — the data layer may have been painted over, resized, or corrupted.',
    );
  }
  const validated = validateData(payload);
  if ('error' in validated) {
    throw new Error(`This PSD’s embedded data is invalid (${validated.error}) — re-export it from Escherllate.`);
  }
  const data = validated.data;
  // No silent fallback (brief §3): without unitRect (the pixel rect of
  // tile-space [0,1]², added to the QR payload with the render-geometry
  // fixes) the paint cannot be registered to tile space — the legacy
  // whole-canvas squash renders every copy uniformly shrunk/offset, which
  // mis-registers rotated placements against each other. Older exports must
  // be re-exported; the painted layers can be copied across in Procreate
  // (same trace + resolution ⇒ identical canvas geometry).
  if (!data.unitRect) {
    throw new Error(
      'This PSD predates precise geometry records — re-export it from Escherllate ' +
        '(you can copy your painted layers from the old PSD into the new one in Procreate).',
    );
  }
  psdDebug('decoded metadata', data);

  const extraction = extractPaintRegions(psd, data.regions, data.colourCount, data.unitRect);

  // §3 sanity: the embedded data must describe THIS file (declared regions
  // present, dimensions match) — otherwise stop with a specific message rather
  // than render a mismatched assumption.
  const check = checkAgainstFile(data, {
    paintRegionNames: extraction.regionsPresent,
    width: psd.width,
    height: psd.height,
  });
  if ('error' in check) {
    throw new Error(`The embedded data doesn’t match this file (${check.error}).`);
  }

  // Non-load-bearing XMP cross-check (§6): log a mismatch if present, never act on it.
  const xmp = validateRenderMetadata(psd.imageResources?.xmpMetadata);
  if ('meta' in xmp && (xmp.meta.wpgId !== data.wpgId || xmp.meta.colourCount !== data.colourCount)) {
    psdDebug('XMP/QR metadata disagree (using QR)', { xmp: xmp.meta, qr: data });
  }

  // Nothing painted → don't render/download a blank wallpaper (brief §3);
  // return the summary so the UI can show a visible "nothing painted" message.
  if (extraction.regions.length === 0) {
    return {
      imported: extraction.imported,
      missingRegions: extraction.missingRegions,
      unmatched: extraction.unmatched,
    };
  }

  const layersStore = useLayersStore();
  const patternStore = usePatternStore();
  const tools = useToolsStore();

  // Snapshot everything we're about to touch, before touching anything.
  const layerSnap = layersStore.captureRenderState();
  const prevConfig = patternStore.currentConfig;
  const prevColourCount = tools.colourCount;

  try {
    layersStore.replaceForRender();

    const byRegion = new Map<
      string,
      { colourInstance?: number; canvas: HTMLCanvasElement; rect: [number, number, number, number] }[]
    >();
    for (const r of extraction.regions) {
      const list = byRegion.get(r.regionName) ?? [];
      list.push({ colourInstance: r.colourInstance, canvas: r.canvas, rect: r.rect });
      byRegion.set(r.regionName, list);
    }
    for (const [regionName, entries] of byRegion) {
      const withInstance = entries.filter((e) => e.colourInstance !== undefined);
      const withoutInstance = entries.filter((e) => e.colourInstance === undefined);
      // All entries of one region share the same rect (same source canvas
      // mapping — every colour instance of a region is the same traced shape
      // painted differently), so the first entry's rect speaks for the group.
      const rect = entries[0]!.rect;
      if (withInstance.length > 0) {
        const instances: Record<number, HTMLCanvasElement> = {};
        for (const e of withInstance) instances[e.colourInstance!] = e.canvas;
        layersStore.addImportedRasterLayerSet(regionName, instances, rect);
      }
      for (const e of withoutInstance) {
        layersStore.addImportedRasterLayer(regionName, e.canvas, e.rect);
      }
    }

    const config: PatternConfig = {
      id: `render:${data.wpgId}`,
      label: getWallpaperGroup(data.wpgId).label,
      wpgId: data.wpgId,
      latticeParams: data.latticeParams,
      artistTile: data.artistTile,
      isSystem: false,
    };
    patternStore.applyConfig(config);
    // Direct assignment, not setColourCount: the render only ever READS this
    // number (planLayerPlacement's instance branch classifies a placement by
    // wpgId+colourCount+transform alone — it never reads tools.colours), and
    // setColourCount would re-derive a default hex palette for no reason.
    tools.colourCount = data.colourCount;

    const canvas = renderWallpaperTiles(tilesAcrossToRings(opts.tilesAcross), TILE_RASTER);
    // Name the output after the imported file (its own name, else the tile name
    // embedded at export time) — never the document that happens to be open.
    const srcBase = source instanceof File ? source.name.replace(/\.[^.]+$/, '') : data.tileId;
    downloadCanvasAsPng(canvas, exportFilename('png', srcBase || data.tileId));
  } finally {
    layersStore.restoreRenderState(layerSnap);
    patternStore.applyConfig(prevConfig);
    tools.colourCount = prevColourCount;
  }

  return {
    imported: extraction.imported,
    missingRegions: extraction.missingRegions,
    unmatched: extraction.unmatched,
  };
}
